import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

export type ButtonVariant = 'default' | 'primary' | 'dark' | 'danger' | 'ghost' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  loading?: boolean | undefined;
}

const variants: Record<ButtonVariant, string> = {
  default: 'bg-surface border-line text-ink hover:border-ink3 shadow-card',
  primary: 'bg-orange border-orange text-on-orange font-bold hover:brightness-95 shadow-card',
  dark: 'bg-ink border-ink text-on-ink hover:brightness-110 shadow-card',
  danger: 'bg-surface border-bad text-bad hover:bg-bad-bg shadow-card',
  ghost: 'bg-transparent border-transparent text-ink2 hover:bg-bg2 hover:text-ink',
  link: 'bg-transparent border-transparent text-orange-ink font-semibold hover:underline p-0 h-auto',
};
const sizes: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-xs',
  md: 'h-[34px] px-3.5 text-[13px]',
  lg: 'h-10 px-4 text-sm',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'default',
    size = 'md',
    loading,
    disabled,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-lg border font-semibold whitespace-nowrap transition-colors disabled:opacity-45 [&>svg]:size-4',
        variants[variant],
        variant !== 'link' && sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="spin" aria-hidden /> : null}
      {children}
    </button>
  );
});
